import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import {
  Receipt, ArrowsClockwise, Coin, ArrowSquareOut, ArrowLeft,
} from "@phosphor-icons/react";

const PROVIDER_LABEL = { stripe: "Stripe", razorpay: "Razorpay" };

export default function History() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptBusy, setReceiptBusy] = useState(null);

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const h = await api.get("/payments/history");
      setCharges(h.data);
    } catch {
      navigate("/", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openReceipt = async (charge) => {
    setReceiptBusy(charge.charge_id);
    try {
      const r = await api.get(`/payments/receipt/${charge.charge_id}`);
      if (r.data.receipt_url) {
        window.open(r.data.receipt_url, "_blank", "noopener");
      } else {
        toast.info(r.data.message || "Receipt not available yet");
      }
    } catch {
      toast.error("Could not fetch receipt");
    } finally {
      setReceiptBusy(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return null;

  const totalPaid = charges.reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="min-h-screen paper-grain" data-testid="history-page">
      <TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/dashboard")}
          className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground hover:text-foreground flex items-center gap-2 mb-10"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Payments</p>
        <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter mt-3 mb-6">
          Every <span className="italic text-accent">receipt</span>, in one place
        </h1>

        <div className="mb-10 grid sm:grid-cols-3 gap-4">
          <div className="border border-border rounded-lg bg-card p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Total spent</p>
            <p className="font-serif text-3xl mt-2">₹{totalPaid.toFixed(0)}</p>
          </div>
          <div className="border border-border rounded-lg bg-card p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Charges</p>
            <p className="font-serif text-3xl mt-2">{charges.length}</p>
          </div>
          <div className="border border-border rounded-lg bg-card p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Last charge</p>
            <p className="font-serif text-3xl mt-2">
              {charges[0]?.created_at ? new Date(charges[0].created_at).toLocaleDateString() : "—"}
            </p>
          </div>
        </div>

        {charges.length === 0 ? (
          <div className="border border-border rounded-lg bg-card p-12 text-center" data-testid="empty-history">
            <Receipt size={40} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <p className="font-serif text-2xl">No charges yet.</p>
            <p className="text-sm text-muted-foreground mt-2 mb-6">
              Buy or subscribe to extra storage — every charge lands here.
            </p>
            <Button
              data-testid="go-to-storage-btn"
              onClick={() => navigate("/storage")}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Get storage
            </Button>
          </div>
        ) : (
          <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="history-list">
            <div className="grid grid-cols-[80px_1fr_120px_100px_120px] gap-4 px-5 py-3 bg-secondary/40 text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
              <span>Date</span>
              <span>Plan</span>
              <span className="text-right">Amount</span>
              <span>Type</span>
              <span className="text-right">Receipt</span>
            </div>
            <ul>
              {charges.map((c) => {
                const d = new Date(c.created_at);
                return (
                  <li
                    key={c.charge_id}
                    data-testid={`charge-${c.charge_id}`}
                    className="grid grid-cols-[80px_1fr_120px_100px_120px] gap-4 px-5 py-4 items-center border-t border-border"
                  >
                    <div>
                      <div className="font-serif text-2xl leading-none">{d.getDate()}</div>
                      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">
                        {d.toLocaleString("en-US", { month: "short", year: "2-digit" })}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.gb ? `${c.gb} GB storage` : c.package_id || "Storage"}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {PROVIDER_LABEL[c.provider] || c.provider}
                        {c.payment_id && ` · ${c.payment_id.slice(0, 16)}…`}
                      </p>
                    </div>
                    <div className="text-right font-serif text-xl">₹{(c.amount || 0).toFixed(0)}</div>
                    <div>
                      <span className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                        c.kind === "subscription_charge"
                          ? "bg-primary/10 text-primary"
                          : "bg-secondary text-secondary-foreground"
                      }`}>
                        {c.kind === "subscription_charge" ? (<><ArrowsClockwise size={12} /> Renewal</>) : (<><Coin size={12} /> One-time</>)}
                      </span>
                    </div>
                    <div className="text-right">
                      <button
                        data-testid={`receipt-btn-${c.charge_id}`}
                        onClick={() => openReceipt(c)}
                        disabled={receiptBusy === c.charge_id}
                        className="text-xs uppercase tracking-[0.15em] font-semibold text-accent hover:underline inline-flex items-center gap-1"
                      >
                        {receiptBusy === c.charge_id ? "…" : (<>Receipt <ArrowSquareOut size={12} /></>)}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
